import {
	InteractionRequiredAuthError,
	InteractionStatus,
} from '@azure/msal-browser';
import { useMsal } from '@azure/msal-react';
import { useEffect, useRef, useState } from 'react';

import {
	clearTokenCache,
	getAccessTokenRequest,
	validateUserAccess,
} from '@/services/msal.service';
import { isUserAuthenticated, logout as msalLogout } from '@/utils/msal';

interface AuthState {
	isAuthenticated: boolean;
	isLoading: boolean;
	error: string | null;
	user: any | null;
}

export const useAuth = () => {
	const { instance, accounts, inProgress } = useMsal();
	const [authState, setAuthState] = useState<AuthState>({
		isAuthenticated: false,
		isLoading: true,
		error: null,
		user: null,
	});

	// Use ref to track the validation interval
	const validationIntervalRef = useRef<number | null>(null);

	const login = async () => {
		try {
			setAuthState((prev) => ({ ...prev, isLoading: true, error: null }));
			await instance.loginRedirect(getAccessTokenRequest());
		} catch (error) {
			setAuthState((prev) => ({
				...prev,
				isLoading: false,
				error: error instanceof Error ? error.message : 'Login failed',
			}));
		}
	};

	const logout = async () => {
		try {
			// Clear the validation interval
			if (validationIntervalRef.current) {
				clearInterval(validationIntervalRef.current);
				validationIntervalRef.current = null;
			}

			clearTokenCache();
			await msalLogout();
		} catch (error) {
			console.error('Logout error:', error);
		}
	};

	const acquireTokenSilent = async () => {
		try {
			const response = await instance.acquireTokenSilent(
				getAccessTokenRequest(),
			);
			return response.accessToken;
		} catch (error) {
			if (error instanceof InteractionRequiredAuthError) {
				await instance.acquireTokenRedirect(getAccessTokenRequest());
			}
			throw error;
		}
	};

	// Periodic validation function
	const validateUserPeriodically = () => {
		if (!validateUserAccess()) {
			console.warn('Periodic validation failed - user access revoked');
			setAuthState((prev) => ({
				...prev,
				isAuthenticated: false,
				error: 'Access revoked - please login again',
			}));
			logout();
		}
	};

	useEffect(() => {
		const checkAuthStatus = () => {
			if (inProgress === InteractionStatus.None) {
				const authenticated = isUserAuthenticated() && validateUserAccess();
				const activeAccount = instance.getActiveAccount();

				setAuthState({
					isAuthenticated: authenticated,
					isLoading: false,
					error: null,
					user: activeAccount,
				});

				// Set up periodic validation if user is authenticated
				if (authenticated) {
					// Clear any existing interval
					if (validationIntervalRef.current) {
						clearInterval(validationIntervalRef.current);
					}

					// Validate user access every 5 minutes
					validationIntervalRef.current = window.setInterval(
						validateUserPeriodically,
						5 * 60 * 1000,
					);
				} else {
					// Clear interval if not authenticated
					if (validationIntervalRef.current) {
						clearInterval(validationIntervalRef.current);
						validationIntervalRef.current = null;
					}
				}
			}
		};

		checkAuthStatus();

		// Cleanup interval on unmount
		return () => {
			if (validationIntervalRef.current) {
				clearInterval(validationIntervalRef.current);
			}
		};
	}, [inProgress, accounts, instance]);

	return {
		...authState,
		login,
		logout,
		acquireTokenSilent,
	};
};
